import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import Welcome from "@/pages/Welcome";
import Login from "@/pages/Login";
import AdminDashboard from "@/pages/AdminDashboard";
import Simulation from "@/pages/Simulation";
import SessionHub from "@/pages/SessionHub";
import SetPassword from "@/pages/SetPassword";
import InfoPage from "@/pages/InfoPages";
import Assessment from "@/pages/Assessment";
import ParticipantReport from "@/pages/ParticipantReport";

function Protected({ children, adminOnly }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-[var(--txt-muted)] font-mono">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/set-password" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/app" replace />;
  return children;
}

// Guards the set-password screen: requires auth, only while a change is pending.
function PasswordChangeGate({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-[var(--txt-muted)] font-mono">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.must_change_password) return <Navigate to={user.role === "admin" ? "/admin" : "/app"} replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/" element={<Welcome />} />
      <Route path="/info/:slug" element={<InfoPage />} />
      <Route path="/assessment" element={<Assessment />} />
      <Route path="/login" element={<Login />} />
      <Route path="/set-password" element={<PasswordChangeGate><SetPassword /></PasswordChangeGate>} />
      <Route path="/admin" element={<Protected adminOnly><AdminDashboard /></Protected>} />
      <Route path="/app" element={<Protected><ParticipantHome /></Protected>} />
      <Route path="/sim/:sessionId" element={<Protected><Simulation /></Protected>} />
      <Route path="/report/:sessionId" element={<Protected><ParticipantReport /></Protected>} />
      <Route path="*" element={<Navigate to={user?.role === "admin" ? "/admin" : "/"} replace />} />
    </Routes>
  );
}

function ParticipantHome() {
  const { user } = useAuth();
  if (user?.role === "admin") return <Navigate to="/admin" replace />;
  return <SessionHub />;
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster theme="dark" position="bottom-right" />
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
